import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { ref, set, get, child } from "firebase/database";
import { auth, database } from "../config/firebase";
import { appAlert } from "../shared/utils/alertBridge";

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: string;
  points: number;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface SignUpProps {
  name: string;
  email: string;
  password?: string;
  avatar?: string;
  whatsapp?: string;
  bio?: string;
  institution?: string;
  cost?: number;
  schedule?: any;
  type?: "user" | "teacher" | "admin";
  age?: number;
  gender?: string;
  country?: string;
  state?: string;
  city?: string;
  isDeaf?: boolean;
  isParentOfDeafPerson?: boolean;
  isLibrasInterpreter?: boolean;
  isLibrasEnthusiast?: boolean;
  librasFluencyLevel?: string;
}

export interface UserProps {
  uid: string;
  name: string;
  email: string;
  type?: string;
  avatar?: string;
  isAdmin?: boolean;
  displayName?: string;
  videoCount?: number;
  maxVideos?: number;
  isDeaf?: boolean;
  createdAt?: number;
  lastLogin?: number;
  age?: number;
  ageGroup?: string;
  gender?: string;
  country?: string;
  state?: string;
  city?: string;
  isParentOfDeafPerson?: boolean;
  isLibrasInterpreter?: boolean;
  isLibrasEnthusiast?: boolean;
  librasFluencyLevel?: string;
  points?: number;
  totalWordsViewed?: number;
  totalSearches?: number;
  totalFavorites?: number;
  badges?: string[];
  photoURL?: string;
  /** Base64 puro ou data URL — mesmo campo usado no app móvel */
  photoBase64?: string | null;
  level?: number;
  streakDays?: number;
  lastActivityDate?: string;
  achievements?: Achievement[];
}

interface AuthContextData {
  user: UserProps;
  loadingAuth: boolean;
  signIn(credentialsSignIn: SignInCredentials): Promise<void>;
  signUp(user: SignUpProps): Promise<void>;
  signOut(): void;
  forgotPassword(email: string): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  updateUserProfile(
    data: Partial<UserProps>,
    options?: { skipSuccessToast?: boolean }
  ): Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/** Firebase Realtime Database não aceita `undefined` em nenhuma propriedade. */
function sanitizeForFirebaseWrite<T extends Record<string, unknown>>(
  obj: T
): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<UserProps>(() => {
    const storagedUser = localStorage.getItem("@bilingua:user");
    if (storagedUser) {
      try {
        return JSON.parse(storagedUser);
      } catch (e) {
        return {} as UserProps;
      }
    }
    return {} as UserProps;
  });
  const [loadingAuth, setLoadingAuth] = useState(true);

  const fetchUserProfile = useCallback(
    async (uid: string): Promise<UserProps | null> => {
      try {
        const usersSnapshot = await get(child(ref(database), `users/${uid}`));
        if (usersSnapshot.exists()) {
          return usersSnapshot.val() as UserProps;
        }

        const teachersSnapshot = await get(
          child(ref(database), `teachers/${uid}`)
        );
        if (teachersSnapshot.exists()) {
          return teachersSnapshot.val() as UserProps;
        }

        return null;
      } catch (error) {
        console.error("Error fetching user profile:", error);
        return null;
      }
    },
    []
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await fetchUserProfile(firebaseUser.uid);
        const base =
          profile ||
          ({
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || "",
            email: firebaseUser.email || "",
          } as UserProps);

        const userData: UserProps = {
          ...base,
          uid: firebaseUser.uid,
          email: base.email || firebaseUser.email || "",
          displayName:
            base.displayName || firebaseUser.displayName || base.name || "",
          name: base.name || base.displayName || firebaseUser.displayName || "",
          photoURL: base.photoURL || firebaseUser.photoURL || undefined,
          avatar:
            (typeof base.avatar === "string" && base.avatar) ||
            firebaseUser.photoURL ||
            "",
        };

        setCurrentUser(userData);
        localStorage.setItem("@bilingua:user", JSON.stringify(userData));
      } else {
        setCurrentUser({} as UserProps);
        localStorage.removeItem("@bilingua:user");
      }
      setLoadingAuth(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  const signIn = useCallback(
    async ({ email, password }: SignInCredentials) => {
      setLoadingAuth(true);

      try {
        const res = await signInWithEmailAndPassword(auth, email, password);
        const { uid } = res.user;

        const profile = await fetchUserProfile(uid);
        const base =
          profile ||
          ({
            uid,
            name: res.user.displayName ?? email,
            email: res.user.email ?? email,
          } as UserProps);
        const userData: UserProps = {
          ...base,
          uid,
          email: base.email || res.user.email || email,
          displayName:
            base.displayName || res.user.displayName || base.name || "",
          name: base.name || base.displayName || res.user.displayName || "",
          photoURL: base.photoURL || res.user.photoURL || undefined,
          avatar:
            (typeof base.avatar === "string" && base.avatar) ||
            res.user.photoURL ||
            "",
        };

        setCurrentUser(userData);
        localStorage.setItem("@bilingua:user", JSON.stringify(userData));

        if (userData.type === "admin" || userData.isAdmin) {
          navigate("/manager");
        } else {
          navigate("/dashboard");
        }
      } catch (err: any) {
        console.error("Erro ao autenticar usuário:", err);

        let message = "Aconteceu algum erro inesperado ao tentar entrar.";
        switch (err.code) {
          case "auth/user-not-found":
          case "auth/wrong-password":
          case "auth/invalid-credential":
          case "auth/invalid-email":
          case "auth/user-disabled":
            message = "Email ou senha incorreta";
            break;
          case "auth/operation-not-allowed":
            message = "Esse tipo de operação não é permitida.";
            break;
          case "auth/weak-password":
            message =
              "A senha que você informou é muito fraca. Tente utilizar uma senha com pelo menos 6 digitos.";
            break;
          default:
            if (err.message) message = err.message;
        }

        appAlert("Oops...", message, "error");
      } finally {
        setLoadingAuth(false);
      }
    },
    [navigate, fetchUserProfile]
  );

  const signUp = useCallback(
    async (signUpData: SignUpProps) => {
      const {
        name,
        email,
        password,
        avatar,
        whatsapp,
        bio,
        institution,
        cost,
        schedule,
        type = "user",
        age,
        gender,
        country,
        state,
        city,
        isDeaf,
        isParentOfDeafPerson,
        isLibrasInterpreter,
        isLibrasEnthusiast,
        librasFluencyLevel,
      } = signUpData;

      setLoadingAuth(true);

      try {
        if (!password) {
          throw new Error("Password is required for email/password signup");
        }
        const res = await createUserWithEmailAndPassword(auth, email, password);
        const { uid } = res.user;

        await updateProfile(res.user, { displayName: name });

        if (type === "teacher") {
          await set(ref(database, `teachers/${uid}`), {
            uid,
            name,
            email,
            avatar: avatar ?? "",
            whatsapp: whatsapp ?? "",
            bio: bio ?? "",
            institution: institution ?? "",
            cost: cost ?? 0,
            schedule: schedule ?? [],
            type,
          });
        } else {
          await set(ref(database, `users/${uid}`), {
            uid,
            name,
            displayName: name,
            email,
            type,
            avatar: avatar ?? "",
            videoCount: 0,
            maxVideos: 3,
            isDeaf: !!isDeaf,
            age: age ?? null,
            gender: gender ?? null,
            country: country ?? null,
            state: state ?? null,
            city: city ?? null,
            isParentOfDeafPerson: isParentOfDeafPerson ?? false,
            isLibrasInterpreter: isLibrasInterpreter ?? false,
            isLibrasEnthusiast: isLibrasEnthusiast ?? false,
            librasFluencyLevel: librasFluencyLevel ?? null,
            createdAt: Date.now(),
            lastLogin: Date.now(),
          });
        }

        const userData = { uid, name, email, type };
        setCurrentUser(userData);
        localStorage.setItem("@bilingua:user", JSON.stringify(userData));

        appAlert(
          "Cadastro realizado!",
          "Seu cadastro foi concluído com sucesso.",
          "success",
          2000
        );

        navigate("/manager");
      } catch (err: any) {
        console.error("Erro no cadastro:", err);

        let message =
          "Aconteceu algum erro inesperado. Entre em contato com o suporte técnico.";
        switch (err.code) {
          case "auth/email-already-in-use":
            message = `O e-mail ${email} já está em uso.`;
            break;
          case "auth/invalid-email":
            message = "E-mail inválido.";
            break;
          case "auth/weak-password":
            message = "A senha é muito fraca. Use pelo menos 6 caracteres.";
            break;
          default:
            if (err.message) message = err.message;
        }

        appAlert("Oops...", message, "error");
      } finally {
        setLoadingAuth(false);
      }
    },
    [navigate]
  );

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem("@bilingua:user");
      setCurrentUser({} as UserProps);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [navigate]);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      appAlert(
        "Sucesso",
        `O link de recuperação de senha foi enviado para o email: ${email}\n\nObs: Não deixe de verificar no spam.`,
        "success"
      );
    } catch (error) {
      appAlert(
        "Oops...",
        `Parece que o email informado não é válido ou não foi encontrado.\n\nEmail: ${email}`,
        "error"
      );
    }
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser?.email) {
        appAlert(
          "Indisponível",
          "Alteração de senha não disponível para esta conta.",
          "error"
        );
        throw new Error("No email on current user");
      }

      try {
        setLoadingAuth(true);
        const credential = EmailAuthProvider.credential(
          firebaseUser.email,
          currentPassword
        );
        await reauthenticateWithCredential(firebaseUser, credential);
        await updatePassword(firebaseUser, newPassword);
        appAlert("Senha atualizada!", "", "success", 1800);
      } catch (error: unknown) {
        const code =
          error && typeof error === "object" && "code" in error
            ? String((error as { code: string }).code)
            : "";
        let message = "Não foi possível alterar a senha.";
        if (code === "auth/wrong-password") {
          message = "Senha atual incorreta.";
        } else if (code === "auth/weak-password") {
          message = "A nova senha é muito fraca.";
        } else if (code === "auth/requires-recent-login") {
          message = "Faça login novamente e tente outra vez.";
        }
        appAlert("Erro", message, "error");
        throw error;
      } finally {
        setLoadingAuth(false);
      }
    },
    []
  );

  const signInWithGoogle = useCallback(async () => {
    setLoadingAuth(true);

    try {
      const provider = new GoogleAuthProvider();
      const res = await signInWithPopup(auth, provider);
      const { uid, displayName, email, photoURL } = res.user;

      let profile = await fetchUserProfile(uid);

      // Se não existe o perfil no database (usuário novo entrando com google), criamos como tipo 'user'.
      if (!profile) {
        const newProfile = {
          uid,
          name: displayName ?? "",
          displayName: displayName ?? "",
          email: email ?? "",
          avatar: photoURL ?? "",
          type: "user",
          videoCount: 0,
          maxVideos: 3,
          isDeaf: false,
          createdAt: Date.now(),
          lastLogin: Date.now(),
        };
        // Use set only if we are sure it's a new user (profile was null after fetch)
        await set(ref(database, `users/${uid}`), newProfile);
        profile = newProfile;
      }

      const userData: UserProps = {
        ...profile,
        uid,
        displayName: profile.displayName || displayName || profile.name || "",
        name: profile.name || profile.displayName || displayName || "",
        email: profile.email || email || "",
        photoURL: profile.photoURL || photoURL || undefined,
        avatar:
          (typeof profile.avatar === "string" && profile.avatar) ||
          photoURL ||
          "",
      };

      setCurrentUser(userData);
      localStorage.setItem("@bilingua:user", JSON.stringify(userData));

      const isProfileIncomplete =
        !userData.gender || (!userData.age && !userData.ageGroup);

      if (userData.type === "admin" || userData.isAdmin) {
        navigate("/manager");
      } else if (isProfileIncomplete) {
        navigate("/complete-registration");
      } else {
        navigate("/dashboard");
      }
    } catch (err: any) {
      console.error("Erro no login com Google:", err);

      if (err.code === "auth/popup-closed-by-user") {
        return;
      }

      appAlert(
        "Oops...",
        "Ocorreu um erro ao tentar entrar com o Google. Tente novamente.",
        "error"
      );
    } finally {
      setLoadingAuth(false);
    }
  }, [navigate, fetchUserProfile]);

  const updateUserProfile = useCallback(
    async (
      data: Partial<UserProps>,
      options?: { skipSuccessToast?: boolean }
    ) => {
      if (!currentUser.uid) return;

      try {
        setLoadingAuth(true);
        const userRef = ref(database, `users/${currentUser.uid}`);

        const merged = {
          ...currentUser,
          ...data,
          lastUpdated: Date.now(),
        };
        const payload = sanitizeForFirebaseWrite(
          merged as unknown as Record<string, unknown>
        );

        await set(userRef, payload);

        const updatedUser = payload as unknown as UserProps;
        setCurrentUser(updatedUser);
        localStorage.setItem("@bilingua:user", JSON.stringify(updatedUser));

        if (!options?.skipSuccessToast) {
          appAlert("Perfil atualizado!", "", "success", 1500);
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        appAlert(
          "Erro ao atualizar",
          "Não foi possível salvar as alterações.",
          "error"
        );
        throw error;
      } finally {
        setLoadingAuth(false);
      }
    },
    [currentUser]
  );

  const providerValue = useMemo(
    () => ({
      signIn,
      signUp,
      signOut,
      forgotPassword,
      changePassword,
      signInWithGoogle,
      updateUserProfile,
      user: currentUser,
      loadingAuth,
    }),
    [
      signIn,
      signUp,
      signOut,
      forgotPassword,
      changePassword,
      signInWithGoogle,
      updateUserProfile,
      currentUser,
      loadingAuth,
    ]
  );

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
